import React from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Users, Target, Globe, Award } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: Users, number: "10,000+", label: "Schools Across India" },
    { icon: Globe, number: "36", label: "States & UTs Covered" },
    { icon: Target, number: "10L+", label: "Active Users" },
    { icon: Award, number: "4.8/5", label: "Avg. Rating" },
  ];

  const values = [
    { icon: Target, title: 'Simplicity First', desc: "We build for teachers and administrators, not IT departments. If it's not simple, it's not done.", color: 'teal' },
    { icon: Globe, title: 'India First', desc: 'Designed for Indian schools with state, district, and board-specific configurations built in.', color: 'emerald' },
    { icon: Award, title: 'Trust & Security', desc: 'Your data is encrypted, isolated, and compliant with Indian data protection standards.', color: 'amber' },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid="about-page">
        {/* Hero */}
        <section className="relative py-24 px-6 bg-slate-900 overflow-hidden">
          <div className="absolute top-[-100px] right-[10%] w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-[120px] pointer-events-none" />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <p className="text-teal-400 text-sm font-semibold uppercase tracking-wider mb-4">About Us</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6" data-testid="about-title">About Ariskool</h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We're on a mission to modernize school administration across India, starting with leave management.
            </p>
          </div>
        </section>

        {/* Story */}
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Ariskool was founded by a team of educators and technologists who saw firsthand how much time schools
                  waste on paper-based leave management. From dusty registers to lost applications, the system was broken.
                </p>
                <p>
                  We built Ariskool specifically for Indian schools — understanding the unique needs of CBSE, ICSE, and
                  State Board institutions across 28 states and 8 Union Territories.
                </p>
                <p>
                  Today, Ariskool serves schools from Srinagar to Kanyakumari, helping them save time, reduce errors,
                  and focus on what truly matters — educating the next generation.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <img
                src="https://images.pexels.com/photos/3231358/pexels-photo-3231358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Indian students in classroom"
                className="rounded-2xl shadow-lg w-full h-72 object-cover"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 px-6 bg-teal-600">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="text-center" data-testid={`about-stat-${idx}`}>
                    <Icon className="h-7 w-7 text-teal-200 mx-auto mb-3" />
                    <div className="text-3xl font-extrabold text-white mb-1">{stat.number}</div>
                    <div className="text-sm text-teal-100">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((v, idx) => {
                const Icon = v.icon;
                return (
                  <div key={idx} className="text-center p-6 rounded-2xl border border-slate-200 hover:shadow-lg hover:border-teal-200 transition-all duration-300">
                    <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-5">
                      <Icon className="h-7 w-7 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default About;
